import React, { createContext, useContext, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Language, SUPPORTED_LANGUAGES, setDefaultLanguage } from './i18n';

interface LanguageContextType {
  t: ReturnType<typeof useTranslation>['t'];
  currentLanguage: string;
  supportedLanguages: Language[];
  changeLanguage: (language: Language) => void;
}

const LanguageContext = createContext<LanguageContextType | undefined>(
  undefined
);

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({
  children,
}) => {
  const { t, i18n } = useTranslation();

  const changeLanguage = (language: Language) => {
    // Use setDefaultLanguage to ensure client translations are loaded
    setDefaultLanguage(language);
  };

  const contextValue: LanguageContextType = {
    t,
    currentLanguage: i18n.language,
    supportedLanguages: SUPPORTED_LANGUAGES,
    changeLanguage,
  };

  return (
    <LanguageContext.Provider value={contextValue}>
      {children}
    </LanguageContext.Provider>
  );
};

// Custom hook to use the language context
export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

// Convenience hook for just getting the translation function
export const useTranslate = () => {
  return useLanguage();
};

// Hook for language switching
export const useLanguageSwitch = () => {
  const { currentLanguage, supportedLanguages, changeLanguage } = useLanguage();
  return {
    currentLanguage,
    supportedLanguages,
    changeLanguage,
  };
};

export default LanguageProvider;
